import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Session, SessionLogin } from '@core/entities/models/session.model';
import { User } from '@core/entities/models/user.model';
import { ApiEndpoints } from '@core/config/api.config';

/**
 * Servicio para gestionar sesiones de usuario.
 * Implementa las operaciones de autenticación según OpenAPI spec.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly http = inject(HttpClient);

  // ---------------------------------------------------------------------------
  // Almacenamiento local del token
  // ---------------------------------------------------------------------------

  private readonly TOKEN_KEY = 'jrs_auth_token';
  private readonly SESSION_KEY = 'jrs_session';
  private readonly USER_KEY = 'jrs_current_user';
  private readonly SESSION_TIMESTAMP_KEY = 'jrs_session_timestamp';
  private readonly SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 horas en milisegundos

  private autoLogoutTimer: any = null;

  // ---------------------------------------------------------------------------
  // API pública - Operaciones de sesión
  // ---------------------------------------------------------------------------

  /**
   * Inicia sesión con las credenciales proporcionadas.
   * POST /sessions (CreateSessionOperation)
   *
   * @param credentials - Email y contraseña del usuario
   * @returns Observable con la sesión creada (incluye token JWT)
   */
  login(credentials: SessionLogin): Observable<Session> {
    return this.http.post<Session>(ApiEndpoints.sessions.login, credentials);
  }

  /**
   * Cierra la sesión actual.
   * POST /sessions/revoke (RevokeSessionOperation)
   *
   * @returns Observable vacío cuando la sesión se cierra correctamente
   */
  logout(): Observable<void> {
    return this.http.post<void>(ApiEndpoints.sessions.logout, {});
  }

  /**
   * Refresca la sesión actual para ampliar su validez.
   * POST /sessions/refresh (RefreshSessionOperation)
   *
   * @returns Observable con la sesión refrescada
   */
  refresh(): Observable<Session> {
    return this.http.post<Session>(ApiEndpoints.sessions.refresh, {});
  }

  /**
   * Obtiene los datos del usuario actual.
   * GET /users/me (GetMeUserOperation)
   *
   * @returns Observable con el usuario actual
   */
  getMe(): Observable<User> {
    return this.http.get<User>(ApiEndpoints.users.me);
  }

  // ---------------------------------------------------------------------------
  // Helpers - Gestión local del token
  // ---------------------------------------------------------------------------

  /**
   * Guarda la sesión y el token en localStorage.
   * También guarda el timestamp de inicio de sesión y configura el auto-logout.
   * NOTA: Esta función ya NO guarda el usuario. Usar saveUser() para eso.
   */
  saveSession(session: Session): void {
    try {
      const now = Date.now();
      localStorage.setItem(this.TOKEN_KEY, session.token);
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
      localStorage.setItem(this.SESSION_TIMESTAMP_KEY, now.toString());

      // Configurar auto-logout después de 24h
      this.setupAutoLogout();
    } catch (e) {}
  }

  /**
   * Guarda el usuario actual en localStorage.
   * Debe llamarse después de obtener el usuario con getMe().
   */
  saveUser(user: User): void {
    try {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    } catch (e) {}
  }

  /**
   * Obtiene el token JWT almacenado.
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Obtiene la sesión almacenada.
   */
  getSession(): Session | null {
    try {
      const raw = localStorage.getItem(this.SESSION_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as Session;
    } catch (e) {
      return null;
    }
  }

  /**
   * Elimina la sesión y el token de localStorage y sessionStorage.
   * También cancela el timer de auto-logout.
   */
  clearSession(): void {
    // Limpiar localStorage completamente
    localStorage.clear();

    // Limpiar sessionStorage completamente
    sessionStorage.clear();

    // Cancelar timer de auto-logout
    if (this.autoLogoutTimer) {
      clearTimeout(this.autoLogoutTimer);
      this.autoLogoutTimer = null;
    }
  }

  /**
   * Comprueba si hay una sesión activa (token presente).
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Obtiene el usuario actual desde localStorage.
   * @returns User del usuario actual o null si no hay usuario guardado
   */
  getCurrentUser(): User | null {
    try {
      const raw = localStorage.getItem(this.USER_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as User;
    } catch (e) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Auto-logout después de 24h
  // ---------------------------------------------------------------------------

  /**
   * Configura un timer para cerrar sesión automáticamente después de 24h.
   * Si ya existe una sesión, calcula el tiempo restante.
   */
  setupAutoLogout(): void {
    // Cancelar timer existente
    if (this.autoLogoutTimer) {
      clearTimeout(this.autoLogoutTimer);
      this.autoLogoutTimer = null;
    }

    const timestampStr = localStorage.getItem(this.SESSION_TIMESTAMP_KEY);
    if (!timestampStr) return;

    const sessionStart = parseInt(timestampStr, 10);
    const now = Date.now();
    const elapsed = now - sessionStart;
    const remaining = this.SESSION_DURATION_MS - elapsed;

    // Si ya pasaron 24h, cerrar sesión inmediatamente
    if (remaining <= 0) {
      this.clearSession();
      // Emitir evento para que la app redirija al login
      window.dispatchEvent(new CustomEvent('session-expired'));
      return;
    }

    // Configurar timer para el tiempo restante
    this.autoLogoutTimer = setTimeout(() => {
      this.clearSession();
      window.dispatchEvent(new CustomEvent('session-expired'));
    }, remaining);
  }

  /**
   * Verifica si la sesión ha expirado (más de 24h desde el inicio).
   * @returns true si la sesión ha expirado
   */
  isSessionExpired(): boolean {
    const timestampStr = localStorage.getItem(this.SESSION_TIMESTAMP_KEY);
    if (!timestampStr) return true;

    const sessionStart = parseInt(timestampStr, 10);
    const now = Date.now();
    const elapsed = now - sessionStart;

    return elapsed >= this.SESSION_DURATION_MS;
  }
}
